import { useAPI } from '@providers/useAPI';
import { isCancelledError, useQueryClient } from '@tanstack/react-query';
import { PendingTopUpResponseDto } from 'app/API';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { createFetchPendingTopUpsQueryFn } from 'app/queries/billing/createFetchPendingTopUpsQueryFn';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuthContext } from '../AuthProvider';
import { type TransactionProcessingEvent, type TransactionResult, useWebsocketContext } from '../WebsocketProvider';

type TProps = {
  children: ReactNode;
};

type TransactionProcessingIdentity = {
  billingAccountId?: string;
  chargeId?: string;
  paymentIntentId?: string;
  settled?: boolean;
};

type TransactionProcessingIndicator = {
  aliases: Set<string>;
  billingAccountId?: string;
  paymentIntentId?: string;
  revision: number;
  settled: boolean;
  timeout: ReturnType<typeof setTimeout>;
  toastId: string;
};

type TRefreshPendingTopUpsOptions = {
  fresh?: boolean;
  isCurrent?: () => boolean;
  requireActiveBillingAccount?: boolean;
};

type TPendingTopUpsRefreshResult = {
  applied: boolean;
  concurrentIndicatorMutations: boolean;
};

type TPendingTopUpsQueryData = {
  indicatorRevisionWatermark: number;
  pendingTopUps: PendingTopUpResponseDto[];
};

type TBalanceAttributionRequest = {
  billingAccountId: string;
  observedActiveBillingAccountEpoch: number;
  observedAuthEpoch: number;
  observedBalance: number;
  observedIndicatorSuccessRevision: number;
  requireAppliedResult: boolean;
  retryDeadlinesMs: number[];
  startedAtMs: number;
};

type TState = {
  transactionStatusDto: TransactionResult | null;
  transactionResults: Map<string, TransactionResult>;
  scheduleBalanceReconciliation: (billingAccountId?: string) => void;
  showTransactionProcessingIndicator: (identity: TransactionProcessingIdentity) => void;
  markTransactionProcessingFailed: (identity: TransactionProcessingIdentity) => void;
};

const TransactionProcessingContext = createContext<TState>({
  transactionStatusDto: null,
  transactionResults: new Map(),
  scheduleBalanceReconciliation: () => {},
  showTransactionProcessingIndicator: () => {},
  markTransactionProcessingFailed: () => {},
});

const normalizeTransactionIdentity = (value: string | undefined): string | undefined => {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
};

const TOAST_DISMISS_COUNTDOWN_SECONDS = 5;
const BALANCE_ATTRIBUTION_RETRY_DEADLINES_MS = [5000, 15000, 45000];

const getTransactionProcessingMessage = (settled: boolean): string =>
  settled
    ? 'Your payment went through. Your balance will update shortly.'
    : 'Your payment is still processing. Your balance will update automatically.';

export const TransactionProcessingProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const { updateMe, isLoggedIn, organization } = useAuthContext();
  const {
    notifyWSUpdate,
    subscribeTransactionProcessingEvents,
    transactionProcessingDisabled: disabled,
  } = useWebsocketContext();
  const queryClient = useQueryClient();

  const [transactionStatusDto, setTransactionStatusDto] = useState<TransactionResult | null>(null);
  const [transactionResults, setTransactionResults] = useState<Map<string, TransactionResult>>(new Map());

  const balanceReconciliationTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const activeBillingAccountIdRef = useRef<string | undefined>(undefined);
  const activeBillingAccountEpochRef = useRef(0);
  const balanceBaselinesRef = useRef<Map<string, number>>(new Map());
  const failedTransactionAliasesRef = useRef<Set<string>>(new Set());
  const indicatorMutationRevisionsRef = useRef<Map<string, number>>(new Map());
  const lastIndicatorSuccessAtRef = useRef<Map<string, number>>(new Map());
  const indicatorSuccessRevisionsRef = useRef<Map<string, number>>(new Map());
  const lastObservedBillingAccountIdRef = useRef<string | undefined>(undefined);
  const lastVisibilityRefreshAtRef = useRef<number | null>(null);
  const pendingTopUpsAuthEpochRef = useRef(0);
  const pendingTopUpsAppliedVersionsRef = useRef<WeakMap<object, Map<number, TPendingTopUpsRefreshResult>>>(
    new WeakMap(),
  );
  const previousActiveBillingAccountIdRef = useRef<string | undefined>(undefined);
  const previousTransactionProcessingEnabledRef = useRef(false);
  const providerMountedRef = useRef(true);
  const transactionProcessingAliasesRef = useRef<Map<string, string>>(new Map());
  const transactionProcessingEnabledRef = useRef(false);
  const transactionProcessingIndicatorsRef = useRef<Map<string, TransactionProcessingIndicator>>(new Map());
  const toastCountdownIntervalsRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());
  const attributeBalanceIncreaseRef = useRef<(attribution: TBalanceAttributionRequest) => void>(() => {});
  const handleTransactionProcessingEventRef = useRef<(event: TransactionProcessingEvent) => void>(() => {});

  const activeBillingAccountId = normalizeTransactionIdentity(organization?.billingAccount?.id);
  const transactionProcessingEnabled = isLoggedIn && !disabled;
  if (previousActiveBillingAccountIdRef.current !== activeBillingAccountId) {
    previousActiveBillingAccountIdRef.current = activeBillingAccountId;
    activeBillingAccountEpochRef.current += 1;
  }
  if (previousTransactionProcessingEnabledRef.current && !transactionProcessingEnabled) {
    pendingTopUpsAuthEpochRef.current += 1;
  }
  previousTransactionProcessingEnabledRef.current = transactionProcessingEnabled;
  activeBillingAccountIdRef.current = activeBillingAccountId;
  transactionProcessingEnabledRef.current = transactionProcessingEnabled;
  const clearBalanceReconciliations = useCallback(() => {
    balanceReconciliationTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    balanceReconciliationTimeoutsRef.current.clear();
  }, []);

  const removeTransactionProcessingIndicator = useCallback((key: string) => {
    const indicator = transactionProcessingIndicatorsRef.current.get(key);
    if (!indicator) {
      return undefined;
    }

    clearTimeout(indicator.timeout);
    transactionProcessingIndicatorsRef.current.delete(key);
    indicator.aliases.forEach((alias) => {
      if (transactionProcessingAliasesRef.current.get(alias) === key) {
        transactionProcessingAliasesRef.current.delete(alias);
      }
    });
    return indicator;
  }, []);

  const dismissToastWithCountdown = useCallback((toastId: string, message: string, variant: 'blank' | 'success') => {
    const existingInterval = toastCountdownIntervalsRef.current.get(toastId);
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    let remaining = TOAST_DISMISS_COUNTDOWN_SECONDS;

    const render = () => {
      const text = `${message} Closing in ${remaining}s`;
      if (variant === 'success') {
        toast.success(text, { id: toastId, duration: Infinity });
        return;
      }
      toast(text, { id: toastId, duration: Infinity });
    };

    render();

    const interval = setInterval(() => {
      remaining -= 1;
      if (remaining > 0) {
        render();
        return;
      }
      clearInterval(interval);
      if (toastCountdownIntervalsRef.current.get(toastId) === interval) {
        toastCountdownIntervalsRef.current.delete(toastId);
      }
      toast.dismiss(toastId);
    }, 1000);

    toastCountdownIntervalsRef.current.set(toastId, interval);
  }, []);

  const fetchPendingTopUpsQueryFn = useMemo(() => createFetchPendingTopUpsQueryFn(call), [call]);

  const discardPendingTopUpsQueries = useCallback(
    (billingAccountId?: string) => {
      const queryKey = billingAccountId
        ? billingKeys.pendingTopUpsByAccount(billingAccountId)
        : billingKeys.pendingTopUps();
      void queryClient.cancelQueries({ exact: !!billingAccountId, queryKey });
      queryClient.removeQueries({ exact: !!billingAccountId, queryKey });
    },
    [queryClient],
  );

  const clearBalanceTracking = useCallback(() => {
    balanceBaselinesRef.current.clear();
    lastIndicatorSuccessAtRef.current.clear();
    indicatorSuccessRevisionsRef.current.clear();
    lastObservedBillingAccountIdRef.current = undefined;
    lastVisibilityRefreshAtRef.current = null;
  }, []);

  const clearTransactionProcessingIndicators = useCallback(() => {
    transactionProcessingIndicatorsRef.current.forEach((indicator) => {
      clearTimeout(indicator.timeout);
      toast.dismiss(indicator.toastId);
    });
    toastCountdownIntervalsRef.current.forEach((interval, toastId) => {
      clearInterval(interval);
      toast.dismiss(toastId);
    });
    toastCountdownIntervalsRef.current.clear();
    transactionProcessingIndicatorsRef.current.clear();
    indicatorMutationRevisionsRef.current.clear();
    transactionProcessingAliasesRef.current.clear();
    failedTransactionAliasesRef.current.clear();
  }, []);

  const bumpIndicatorMutationRevision = useCallback((billingAccountId?: string) => {
    if (!billingAccountId) {
      return 0;
    }
    const revision = (indicatorMutationRevisionsRef.current.get(billingAccountId) ?? 0) + 1;
    indicatorMutationRevisionsRef.current.set(billingAccountId, revision);
    return revision;
  }, []);

  const showTransactionProcessingIndicator = useCallback(
    ({ billingAccountId, chargeId, paymentIntentId, settled = false }: TransactionProcessingIdentity) => {
      if (!transactionProcessingEnabledRef.current) {
        return;
      }

      const normalizedBillingAccountId = normalizeTransactionIdentity(billingAccountId);
      const normalizedChargeId = normalizeTransactionIdentity(chargeId);
      const normalizedPaymentIntentId = normalizeTransactionIdentity(paymentIntentId);
      const aliases = [normalizedChargeId, normalizedPaymentIntentId].filter(
        (value): value is string => value !== undefined,
      );
      if (aliases.some((alias) => failedTransactionAliasesRef.current.has(alias))) {
        return;
      }
      const existingKey = aliases
        .map((alias) => transactionProcessingAliasesRef.current.get(alias))
        .find((key): key is string => key !== undefined);

      if (existingKey) {
        const existingIndicator = transactionProcessingIndicatorsRef.current.get(existingKey);
        if (existingIndicator) {
          const identityChanged =
            (!existingIndicator.billingAccountId && !!normalizedBillingAccountId) ||
            (!existingIndicator.paymentIntentId && !!normalizedPaymentIntentId);
          existingIndicator.billingAccountId ??= normalizedBillingAccountId;
          existingIndicator.paymentIntentId ??= normalizedPaymentIntentId;
          if (identityChanged) {
            existingIndicator.revision = bumpIndicatorMutationRevision(existingIndicator.billingAccountId);
          }
          if (settled && !existingIndicator.settled) {
            existingIndicator.settled = true;
            toast.loading(getTransactionProcessingMessage(true), {
              id: existingIndicator.toastId,
              duration: Infinity,
            });
          }
        }
        aliases.forEach((alias) => {
          existingIndicator?.aliases.add(alias);
          transactionProcessingAliasesRef.current.set(alias, existingKey);
        });
        return;
      }

      const key = normalizedChargeId ?? normalizedPaymentIntentId;
      if (!key) {
        return;
      }

      const toastId = `transaction-processing-${key}`;
      toast.loading(getTransactionProcessingMessage(settled), {
        id: toastId,
        duration: Infinity,
      });

      const timeout = setTimeout(() => {
        const indicator = removeTransactionProcessingIndicator(key);
        if (!indicator) {
          return;
        }
        dismissToastWithCountdown(
          indicator.toastId,
          'Your payment is still processing. Your balance will update automatically.',
          'blank',
        );
      }, 60000);
      const indicator: TransactionProcessingIndicator = {
        aliases: new Set(aliases),
        billingAccountId: normalizedBillingAccountId,
        paymentIntentId: normalizedPaymentIntentId,
        revision: bumpIndicatorMutationRevision(normalizedBillingAccountId),
        settled,
        timeout,
        toastId,
      };
      transactionProcessingIndicatorsRef.current.set(key, indicator);
      aliases.forEach((alias) => transactionProcessingAliasesRef.current.set(alias, key));
    },
    [bumpIndicatorMutationRevision, dismissToastWithCountdown, removeTransactionProcessingIndicator],
  );

  const resolveTransactionProcessingIndicatorAsSuccess = useCallback(
    (key: string) => {
      const indicator = removeTransactionProcessingIndicator(key);
      if (indicator) {
        if (indicator.billingAccountId) {
          discardPendingTopUpsQueries(indicator.billingAccountId);
          lastIndicatorSuccessAtRef.current.set(indicator.billingAccountId, Date.now());
          indicatorSuccessRevisionsRef.current.set(
            indicator.billingAccountId,
            (indicatorSuccessRevisionsRef.current.get(indicator.billingAccountId) ?? 0) + 1,
          );
        } else {
          discardPendingTopUpsQueries();
        }
        dismissToastWithCountdown(indicator.toastId, 'Your balance has been updated.', 'success');
      }
    },
    [discardPendingTopUpsQueries, dismissToastWithCountdown, removeTransactionProcessingIndicator],
  );

  const resolveTransactionProcessingIndicator = useCallback(
    (result: TransactionResult) => {
      if (result.status === 'pending') {
        return false;
      }

      const resultId = normalizeTransactionIdentity(result.id);
      const key = resultId ? transactionProcessingAliasesRef.current.get(resultId) : undefined;

      if (result.status === 'success') {
        if (key) {
          resolveTransactionProcessingIndicatorAsSuccess(key);
          return true;
        }
        discardPendingTopUpsQueries();
        return false;
      }

      if (resultId) {
        failedTransactionAliasesRef.current.add(resultId);
      }
      const indicator = key ? removeTransactionProcessingIndicator(key) : undefined;
      indicator?.aliases.forEach((alias) => failedTransactionAliasesRef.current.add(alias));
      if (indicator) {
        toast.dismiss(indicator.toastId);
      }
      return false;
    },
    [discardPendingTopUpsQueries, removeTransactionProcessingIndicator, resolveTransactionProcessingIndicatorAsSuccess],
  );

  const markTransactionProcessingFailed = useCallback(
    ({ chargeId, paymentIntentId }: TransactionProcessingIdentity) => {
      const identities = [normalizeTransactionIdentity(chargeId), normalizeTransactionIdentity(paymentIntentId)];
      identities.forEach((identity) => {
        if (!identity) {
          return;
        }
        failedTransactionAliasesRef.current.add(identity);
        const key = transactionProcessingAliasesRef.current.get(identity);
        const indicator = key ? removeTransactionProcessingIndicator(key) : undefined;
        indicator?.aliases.forEach((alias) => failedTransactionAliasesRef.current.add(alias));
        if (indicator) {
          toast.dismiss(indicator.toastId);
        }
      });
    },
    [removeTransactionProcessingIndicator],
  );

  const applyPendingTopUps = useCallback(
    (billingAccountId: string, pendingTopUps: PendingTopUpResponseDto[], indicatorRevisionWatermark: number) => {
      const pendingPaymentIntentIds = new Set<string>();
      pendingTopUps.forEach((pendingTopUp) => {
        const paymentIntentId = normalizeTransactionIdentity(pendingTopUp.paymentIntentId);
        if (!paymentIntentId) {
          return;
        }
        pendingPaymentIntentIds.add(paymentIntentId);
        showTransactionProcessingIndicator({
          billingAccountId,
          paymentIntentId,
          settled: pendingTopUp.settled,
        });
      });

      const resolvedKeys: string[] = [];
      const dismissedKeys: string[] = [];
      transactionProcessingIndicatorsRef.current.forEach((indicator, key) => {
        if (
          indicator.billingAccountId === billingAccountId &&
          indicator.paymentIntentId &&
          indicator.revision <= indicatorRevisionWatermark &&
          !pendingPaymentIntentIds.has(indicator.paymentIntentId)
        ) {
          if (indicator.settled) {
            resolvedKeys.push(key);
          } else {
            dismissedKeys.push(key);
          }
        }
      });
      resolvedKeys.forEach(resolveTransactionProcessingIndicatorAsSuccess);
      dismissedKeys.forEach((key) => {
        const indicator = removeTransactionProcessingIndicator(key);
        if (indicator) {
          toast.dismiss(indicator.toastId);
        }
      });
    },
    [
      removeTransactionProcessingIndicator,
      resolveTransactionProcessingIndicatorAsSuccess,
      showTransactionProcessingIndicator,
    ],
  );

  const refreshPendingTopUps = useCallback(
    async (
      billingAccountId = activeBillingAccountIdRef.current,
      { fresh = false, isCurrent, requireActiveBillingAccount = true }: TRefreshPendingTopUpsOptions = {},
    ) => {
      const notApplied = { applied: false, concurrentIndicatorMutations: false };
      if (!providerMountedRef.current || !transactionProcessingEnabledRef.current || !billingAccountId) {
        return notApplied;
      }

      const requestIsCurrent = () =>
        providerMountedRef.current &&
        transactionProcessingEnabledRef.current &&
        (!requireActiveBillingAccount || activeBillingAccountIdRef.current === billingAccountId) &&
        (!isCurrent || isCurrent());
      const queryKey = billingKeys.pendingTopUpsByAccount(billingAccountId);

      if (fresh) {
        const queryBeforeCancellation = queryClient.getQueryCache().find({ exact: true, queryKey });
        await queryClient.cancelQueries({ exact: true, queryKey });
        if (!requestIsCurrent()) {
          return notApplied;
        }
        if (
          queryBeforeCancellation &&
          queryClient.getQueryCache().find({ exact: true, queryKey }) === queryBeforeCancellation
        ) {
          queryClient.removeQueries({ exact: true, queryKey });
        }
      }

      if (!requestIsCurrent()) {
        return notApplied;
      }

      const queryFn = async (
        context: Parameters<typeof fetchPendingTopUpsQueryFn>[0],
      ): Promise<TPendingTopUpsQueryData> => {
        const indicatorRevisionWatermark = indicatorMutationRevisionsRef.current.get(billingAccountId) ?? 0;
        const pendingTopUps = await fetchPendingTopUpsQueryFn(context);
        return { indicatorRevisionWatermark, pendingTopUps };
      };
      const fetchPromise = queryClient.fetchQuery({
        queryFn,
        queryKey,
        structuralSharing: false,
      });
      const queryForFetch = queryClient.getQueryCache().find({ exact: true, queryKey });

      let data: TPendingTopUpsQueryData;
      try {
        data = await fetchPromise;
      } catch (error) {
        if (
          !isCancelledError(error) &&
          requestIsCurrent() &&
          queryForFetch &&
          queryClient.getQueryCache().find({ exact: true, queryKey }) === queryForFetch
        ) {
          console.error('Unable to retrieve pending top-ups', error);
        }
        return notApplied;
      }

      if (!queryForFetch || !requestIsCurrent()) {
        return notApplied;
      }

      const dataUpdateCount = queryForFetch.state.dataUpdateCount;
      const appliedVersions = pendingTopUpsAppliedVersionsRef.current.get(queryForFetch);
      const appliedResult = appliedVersions?.get(dataUpdateCount);
      if (appliedResult) {
        return appliedResult;
      }

      if (queryClient.getQueryCache().find({ exact: true, queryKey }) !== queryForFetch) {
        return notApplied;
      }

      const concurrentIndicatorMutations =
        (indicatorMutationRevisionsRef.current.get(billingAccountId) ?? 0) !== data.indicatorRevisionWatermark;
      applyPendingTopUps(billingAccountId, data.pendingTopUps, data.indicatorRevisionWatermark);
      const result = { applied: true, concurrentIndicatorMutations };
      const queryAppliedVersions = appliedVersions ?? new Map<number, TPendingTopUpsRefreshResult>();
      queryAppliedVersions.set(dataUpdateCount, result);
      if (!appliedVersions) {
        pendingTopUpsAppliedVersionsRef.current.set(queryForFetch, queryAppliedVersions);
      }
      return result;
    },
    [applyPendingTopUps, fetchPendingTopUpsQueryFn, queryClient],
  );

  const attributeBalanceIncrease = useCallback(
    (attribution: TBalanceAttributionRequest) => {
      const {
        billingAccountId,
        observedActiveBillingAccountEpoch,
        observedAuthEpoch,
        observedBalance,
        observedIndicatorSuccessRevision,
        requireAppliedResult,
        retryDeadlinesMs,
        startedAtMs,
      } = attribution;
      const attributionIsCurrent = () =>
        providerMountedRef.current &&
        transactionProcessingEnabledRef.current &&
        activeBillingAccountEpochRef.current === observedActiveBillingAccountEpoch &&
        pendingTopUpsAuthEpochRef.current === observedAuthEpoch &&
        activeBillingAccountIdRef.current === billingAccountId &&
        balanceBaselinesRef.current.get(billingAccountId) === observedBalance;
      const scheduleNextAttempt = () => {
        const [nextDeadlineMs, ...remainingDeadlinesMs] = retryDeadlinesMs;
        if (nextDeadlineMs === undefined) {
          return;
        }
        const timeout = setTimeout(
          () => {
            balanceReconciliationTimeoutsRef.current.delete(timeout);
            attributeBalanceIncreaseRef.current({
              ...attribution,
              requireAppliedResult: true,
              retryDeadlinesMs: remainingDeadlinesMs,
            });
          },
          Math.max(0, startedAtMs + nextDeadlineMs - Date.now()),
        );
        balanceReconciliationTimeoutsRef.current.add(timeout);
      };

      const chainTerminated = () =>
        !attributionIsCurrent() ||
        (indicatorSuccessRevisionsRef.current.get(billingAccountId) ?? 0) !== observedIndicatorSuccessRevision ||
        (() => {
          const lastIndicatorSuccessAt = lastIndicatorSuccessAtRef.current.get(billingAccountId);
          return lastIndicatorSuccessAt !== undefined && Date.now() - lastIndicatorSuccessAt < 10000;
        })();

      if (chainTerminated()) {
        return;
      }

      void (async () => {
        const result = await refreshPendingTopUps(billingAccountId, {
          fresh: true,
          isCurrent: () => !chainTerminated(),
        });
        if (chainTerminated()) {
          return;
        }
        let hasActiveIndicator = false;
        transactionProcessingIndicatorsRef.current.forEach((indicator) => {
          if (indicator.billingAccountId === billingAccountId) {
            hasActiveIndicator = true;
          }
        });
        if (!result?.applied) {
          if (hasActiveIndicator || requireAppliedResult) {
            scheduleNextAttempt();
            return;
          }
        } else if (hasActiveIndicator && result.concurrentIndicatorMutations) {
          return;
        }
        dismissToastWithCountdown(`balance-updated-${billingAccountId}`, 'Your balance has been updated.', 'success');
      })();
    },
    [dismissToastWithCountdown, refreshPendingTopUps],
  );

  useEffect(() => {
    attributeBalanceIncreaseRef.current = attributeBalanceIncrease;
  }, [attributeBalanceIncrease]);

  useEffect(() => {
    if (!isLoggedIn || disabled) {
      return;
    }

    const currentBalance = organization?.billingAccount?.currentBalance;
    if (!activeBillingAccountId || typeof currentBalance !== 'number') {
      return;
    }

    const activeBillingAccountChanged = lastObservedBillingAccountIdRef.current !== activeBillingAccountId;
    const previousBalance = balanceBaselinesRef.current.get(activeBillingAccountId);
    lastObservedBillingAccountIdRef.current = activeBillingAccountId;
    balanceBaselinesRef.current.set(activeBillingAccountId, currentBalance);

    if (activeBillingAccountChanged || previousBalance === undefined || currentBalance <= previousBalance) {
      return;
    }

    attributeBalanceIncrease({
      billingAccountId: activeBillingAccountId,
      observedActiveBillingAccountEpoch: activeBillingAccountEpochRef.current,
      observedAuthEpoch: pendingTopUpsAuthEpochRef.current,
      observedBalance: currentBalance,
      observedIndicatorSuccessRevision: indicatorSuccessRevisionsRef.current.get(activeBillingAccountId) ?? 0,
      requireAppliedResult: false,
      retryDeadlinesMs: BALANCE_ATTRIBUTION_RETRY_DEADLINES_MS,
      startedAtMs: Date.now(),
    });
  }, [
    activeBillingAccountId,
    attributeBalanceIncrease,
    disabled,
    isLoggedIn,
    organization?.billingAccount?.currentBalance,
  ]);

  const scheduleBalanceReconciliation = useCallback(
    (billingAccountId?: string) => {
      if (!isLoggedIn || disabled) {
        return;
      }

      const reconciliationBillingAccountId =
        normalizeTransactionIdentity(billingAccountId) ?? activeBillingAccountIdRef.current;
      [5000, 15000, 45000].forEach((delay) => {
        const timeout = setTimeout(() => {
          balanceReconciliationTimeoutsRef.current.delete(timeout);
          void updateMe({ includeNotifications: false });
          if (reconciliationBillingAccountId) {
            void refreshPendingTopUps(reconciliationBillingAccountId, { requireActiveBillingAccount: false });
          }
          notifyWSUpdate();
        }, delay);
        balanceReconciliationTimeoutsRef.current.add(timeout);
      });
    },
    [disabled, isLoggedIn, notifyWSUpdate, refreshPendingTopUps, updateMe],
  );

  useEffect(() => {
    if (!isLoggedIn || disabled || !activeBillingAccountId) {
      return;
    }
    void refreshPendingTopUps(activeBillingAccountId);
  }, [activeBillingAccountId, disabled, isLoggedIn, refreshPendingTopUps]);

  useEffect(() => {
    if (!isLoggedIn) {
      lastVisibilityRefreshAtRef.current = null;
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      const now = Date.now();
      const lastRefreshAt = lastVisibilityRefreshAtRef.current;
      if (lastRefreshAt !== null && now - lastRefreshAt < 5000) {
        return;
      }

      lastVisibilityRefreshAtRef.current = now;
      void updateMe({ includeNotifications: false });
      void refreshPendingTopUps();
      notifyWSUpdate();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isLoggedIn, notifyWSUpdate, refreshPendingTopUps, updateMe]);
  const flashTransactionStatus = useCallback((value: TransactionResult) => {
    setTransactionResults((current) => {
      const next = new Map(current);
      next.delete(value.id);
      next.set(value.id, value);
      if (next.size > 20) {
        const oldestId = next.keys().next().value;
        if (typeof oldestId === 'string') {
          next.delete(oldestId);
        }
      }
      return next;
    });
    setTransactionStatusDto(value);
    setTimeout(() => setTransactionStatusDto(null), 1000);
  }, []);

  handleTransactionProcessingEventRef.current = (event) => {
    if (event.type === 'organization-updated') {
      const billingAccountId = activeBillingAccountIdRef.current;
      const hasPendingPaymentForBillingAccount = Array.from(transactionProcessingIndicatorsRef.current.values()).some(
        (indicator) => indicator.billingAccountId === billingAccountId && !!indicator.paymentIntentId,
      );
      if (billingAccountId && hasPendingPaymentForBillingAccount) {
        void refreshPendingTopUps(billingAccountId);
      }
      return;
    }

    if (event.type === 'transaction-status') {
      flashTransactionStatus(event.data);
      const matchedIndicator = resolveTransactionProcessingIndicator(event.data);
      if (event.data.status === 'success' && !matchedIndicator) {
        void refreshPendingTopUps(activeBillingAccountIdRef.current, { fresh: true });
      }
      return;
    }

    void refreshPendingTopUps(activeBillingAccountIdRef.current, { fresh: true });
  };

  useEffect(
    () =>
      subscribeTransactionProcessingEvents((event) => {
        handleTransactionProcessingEventRef.current(event);
      }),
    [subscribeTransactionProcessingEvents],
  );

  useEffect(() => {
    if (transactionProcessingEnabled) {
      return;
    }
    clearBalanceReconciliations();
    clearBalanceTracking();
    clearTransactionProcessingIndicators();
    discardPendingTopUpsQueries();
    pendingTopUpsAppliedVersionsRef.current = new WeakMap();
  }, [
    clearBalanceReconciliations,
    clearBalanceTracking,
    clearTransactionProcessingIndicators,
    discardPendingTopUpsQueries,
    transactionProcessingEnabled,
  ]);

  useEffect(() => {
    return () => {
      providerMountedRef.current = false;
      pendingTopUpsAuthEpochRef.current += 1;
      discardPendingTopUpsQueries();
      pendingTopUpsAppliedVersionsRef.current = new WeakMap();
      transactionProcessingEnabledRef.current = false;
      clearBalanceReconciliations();
      clearBalanceTracking();
      clearTransactionProcessingIndicators();
    };
  }, [
    clearBalanceReconciliations,
    clearBalanceTracking,
    clearTransactionProcessingIndicators,
    discardPendingTopUpsQueries,
  ]);

  return (
    <TransactionProcessingContext.Provider
      value={{
        transactionStatusDto,
        transactionResults,
        scheduleBalanceReconciliation,
        showTransactionProcessingIndicator,
        markTransactionProcessingFailed,
      }}
    >
      {children}
    </TransactionProcessingContext.Provider>
  );
};

export const useTransactionProcessingContext = () => useContext(TransactionProcessingContext);
